import { Component, createEffect, createMemo, createSignal, on, onCleanup, Show } from 'solid-js'
import { A, useLocation, useNavigate, useSearchParams } from '@solidjs/router'
import Alert from '../../shared/Alert'
import Divider from '../../shared/Divider'
import { ACCOUNT_KEY, settingStore, toastStore, userStore } from '../../store'
import { getStrictForm, setComponentPageTitle, storage } from '../../shared/util'
import TextInput from '../../shared/TextInput'
import Button from '../../shared/Button'
import { isLoggedIn } from '/web/store/api'
import { TitleCard } from '/web/shared/Card'
import { createStore } from 'solid-js/store'
import { wait } from '/common/util'
import LoginFormModal from './LoginFormModal'
import { Page } from '/web/Layout'
import { useGoogleReady } from '/web/shared/hooks'
import PageHeader from '/web/shared/PageHeader'
import RegisterFormModal from './RegisterFormModal'
import { EVENTS, events } from '/web/emitter'

const LoginPage: Component = () => {
  const store = userStore()
  const cfg = settingStore()
  const [query] = useSearchParams()

  const [register, setRegister] = createSignal(true)

  createEffect(() => {
    setRegister(store.loginModalMode === 'register')
  })

  /** Friendly error message passed out of the mutation, if it exists. */
  const loginError = createMemo(() => {
    if (!store.error) return null
    if (store.error.includes('NetworkError')) {
      return "We couldn't reach our servers."
    }

    return 'Something went wrong.'
  })

  return (
    <Page class="flex w-full flex-col items-center">
      <div class="w-full max-w-sm">
        <Show when={register()}>
          <RegisterForm isLoading={store.loading} onClickLogin={() => setRegister(false)} />
        </Show>
        <Show when={!register()}>
          <LoginForm isLoading={store.loading} onClickRegister={() => setRegister(true)} />
        </Show>
        <Show when={loginError()}>
          <Divider />
          <Alert schema="error" title="Failed to log in.">
            {loginError()}
          </Alert>
        </Show>
      </div>
    </Page>
  )
}

export default LoginPage

type FormProps = { isLoading: boolean; onClickRegister?: () => void; onClickLogin?: () => void }

const RegisterForm: Component<FormProps> = (props) => {
  const [store, setStore] = createStore({ handle: '', username: '', password: '', confirm: '' })
  const navigate = useNavigate()
  const register = (evt: Event) => {
    const { username, password, email } = getStrictForm(evt, {
      // handle: 'string',
      username: 'string',
      email: 'string',
      password: 'string',
      // confirm: 'string',
    })

    let userHandle = username

    if (!username || !password || !email) return

    // if (password !== confirm) {
    //   toastStore.warn('Passwords do not match', 2)
    //   return
    // }
    try {
      userStore.register({ handle: userHandle, username, password, email }, () => {
        const redirect = userStore.getState().loginModalRedirect
        if (redirect) {
          window.location.href = redirect
        } else {
          userStore.loginModal(false)
          window.location.href = window.location.pathname + '?showTokens=1'
        }
      })
    } catch (error: any) {
      console.log(error)
      toastStore.warn(error.message, 2)
    }
  }

  return <RegisterFormModal onClickLogin={props.onClickLogin} onClickRegister={register} />

  return (
    <form onSubmit={register} class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <TextInput
          label="Display Name"
          placeholder="Display name"
          required
          onChange={(ev) => setStore('handle', ev.currentTarget.value)}
        />
        <TextInput
          label="Username"
          fieldName="username"
          placeholder="Username"
          required
          onChange={(ev) => setStore('username', ev.currentTarget.value)}
        />
        <TextInput
          label="Password"
          placeholder="Password"
          type="password"
          onChange={(ev) => setStore('password', ev.currentTarget.value)}
          required
        />
        <TextInput
          placeholder="Confirm Password"
          type="password"
          required
          onChange={(ev) => setStore('confirm', ev.currentTarget.value)}
          onKeyDown={(ev) => (ev.key === 'Enter' ? register() : null)}
        />
      </div>

      <Button disabled={props.isLoading} onClick={register}>
        {props.isLoading ? 'Registering...' : 'Register'}
      </Button>
    </form>
  )
}

const LoginForm: Component<FormProps> = (props) => {
  let refGoogle: any
  const navigate = useNavigate()
  const [query] = useSearchParams()
  const loc = useLocation()
  const state = settingStore()
  const user = userStore()

  const [error, setError] = createSignal<string>()
  const google = useGoogleReady()

  const [store, setStore] = createStore({
    username: loc.pathname.includes('/remember') ? storage.localGetItem(ACCOUNT_KEY) || '' : '',
    password: '',
  })

  createEffect(() => {
    if (state.initLoading) return

    if (query.callback && isLoggedIn()) {
      for (const authUrl of state.config.authUrls) {
        if (query.callback.startsWith(authUrl)) return handleLogin()
      }
      setError('Invalid callback URL')
      return
    }
  })

  createEffect(
    on(
      () => google(),
      () => {
        const win: any = window
        const api = win.google?.accounts?.id

        if (!api) return

        if (state.config.serverConfig?.googleClientId) {
          api.initialize({
            client_id: state.config.serverConfig?.googleClientId,
            callback: (result: any) => {
              userStore.handleGoogleCallback('login', result, () => navigate('/dashboard'))
            },
          })

          api.renderButton(refGoogle, {
            theme: 'filled_black',
            size: 'large',
            type: 'standard',
            text: 'signin_with',
          })
        }
      }
    )
  )

  const handleLogin = () => {
    userStore.thirdPartyLogin((token) => {
      location.href = `${query.callback}?access_token=${token}`
    })
  }

  const login = () => {
    const { username, password } = store
    if (!username || !password) return

    userStore.login(username, password, async () => {
      if (query.callback) {
        for (const authUrl of state.config.authUrls) {
          if (!query.callback.startsWith(authUrl)) continue
          await wait(0.1)
          return handleLogin()
        }
      }

      userStore.loginModal(false)

      // navigate('/dashboard')
      window.location.reload()
    })
  }

  const onLoggedIn = () => {
    window.location.reload()
  }
  events.on(EVENTS.loggedIn, onLoggedIn)
  onCleanup(() => events.removeListener(EVENTS.loggedIn, onLoggedIn))

  return (
    <LoginFormModal
      onClickLogin={login}
      onClickRegister={props.onClickRegister}
      setStore={setStore}
    />
  )

  return (
    <form class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <label class="font-clash font-semibold" for="username">
          Username or Email
        </label>
        <TextInput
          placeholder="Username"
          disabled={user.loggedIn}
          required
          value={store.username}
          onChange={(ev) => setStore('username', ev.currentTarget.value)}
        />
        <TextInput
          placeholder="Password"
          type="password"
          required
          disabled={user.loggedIn}
          onChange={(ev) => setStore('password', ev.currentTarget.value)}
          onKeyDown={(ev) => (ev.key === 'Enter' ? login() : null)}
        />
      </div>

      <Show when={error()}>
        <TitleCard type="rose">{error()}</TitleCard>
      </Show>

      <Button onClick={login} disabled={user.loggedIn || props.isLoading || !!error()}>
        {props.isLoading ? 'Logging in...' : 'Login'}
      </Button>

      <div
        class="flex justify-center"
        classList={{ hidden: user.loggedIn }}
        ref={(ref) => {
          refGoogle = ref
        }}
        id="g_id_onload"
        data-context="signin"
        data-ux_mode="popup"
        data-login_uri={`${location.origin}/oauth/google`}
        data-itp_support="true"
      ></div>
    </form>
  )
}
